import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchCourseSections,
  createCourseSection,
  reorderCourseSections,
  createCourseVideo,
  reorderCourseVideos,
  uploadTrainerVideo,
  generateCourseSectionsAI,
  generateSectionVideosAI,
  generateVideoDescriptionAI,
  generateVideoThumbnailAI,
  uploadVideoThumbnail,
  deleteVideoThumbnail,
  getVideoThumbnail,
  deleteCourseVideo,
  deleteCourseSection,
} from "@/api/trainerAuth";

export default function TrainerCourseContent() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [courseTitle, setCourseTitle] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiVideoLoading, setAiVideoLoading] = useState(null);
  const [aiDescriptionLoading, setAiDescriptionLoading] = useState(null);
  const [aiThumbnailLoading, setAiThumbnailLoading] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCourseSections(courseId);
      setSections(data);
      
      // الحصول على عنوان الكورس
      if (data.length > 0 && data[0].course_title) {
        setCourseTitle(data[0].course_title);
      }
      
      // توسيع أول سكشن بشكل افتراضي
      if (data.length > 0 && !expandedSections[data[0].id]) {
        setExpandedSections(prev => ({ ...prev, [data[0].id]: true }));
      }
    } catch (e) {
      console.error(e);
      setError("فشل تحميل المحتوى");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  /* ================= Sections ================= */
  const handleAddSection = async () => {
    if (!newSectionTitle.trim()) {
      setError("يرجى إدخال عنوان للسكشن");
      return;
    }
    
    setError("");
    setSuccess("");
    try {
      await createCourseSection(courseId, { title: newSectionTitle });
      setSuccess("تم إضافة السكشن بنجاح");
      setNewSectionTitle("");
      loadSections();
    } catch (e) {
      console.error(e);
      setError("فشل إضافة السكشن");
    }
  };

  const handleGenerateSectionsAI = async () => {
    if (!window.confirm("سيتم إنشاء سكشنات تلقائيًا بناءً على محتوى الكورس، هل أنت متأكد؟")) return;
    
    setAiLoading(true);
    setError("");
    setSuccess("");
    try {
      await generateCourseSectionsAI(courseId);
      setSuccess("تم توليد السكشنات بنجاح");
      await loadSections();
    } catch (e) {
      console.error(e);
      setError("فشل توليد السكشنات");
    } finally {
      setAiLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId, sectionTitle) => {
    if (!window.confirm(`هل أنت متأكد من حذف السكشن "${sectionTitle}"؟ هذا سيمسح جميع الفيديوهات داخله أيضًا.`)) return;
    
    try {
      await deleteCourseSection(sectionId);
      setSuccess(`تم حذف السكشن "${sectionTitle}" بنجاح`);
      loadSections();
    } catch (e) {
      console.error(e);
      setError("فشل حذف السكشن");
    }
  };

  const moveSection = async (index, direction) => {
    const updated = [...sections];
    const target = index + direction;
    if (target < 0 || target >= updated.length) return;
    
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setSections(updated);

    try {
      await reorderCourseSections(
        courseId,
        updated.map((s, i) => ({ id: s.id, order: i + 1 }))
      );
      setSuccess("تم إعادة ترتيب السكشنات بنجاح");
    } catch (e) {
      console.error(e);
      setError("فشل إعادة الترتيب");
    }
  };

  /* ================= Videos ================= */
  const handleAddVideo = async (sectionId, video) => {
    try {
      await createCourseVideo(sectionId, video);
      setSuccess("تم إضافة الفيديو بنجاح");
      loadSections();
    } catch (e) {
      console.error(e);
      setError("فشل إضافة الفيديو");
    }
  };

  const handleDeleteVideo = async (videoId, videoTitle) => {
    if (!window.confirm(`هل أنت متأكد من حذف الفيديو "${videoTitle}"؟`)) return;
    
    try {
      await deleteCourseVideo(videoId);
      setSuccess(`تم حذف الفيديو "${videoTitle}" بنجاح`);
      loadSections();
    } catch (e) {
      console.error(e);
      setError("فشل حذف الفيديو");
    }
  };

  const handleGenerateVideosAI = async (sectionId, sectionTitle) => {
    if (!window.confirm(`توليد عناوين فيديوهات تلقائيًا للسكشن "${sectionTitle}"؟`)) return;
    
    setAiVideoLoading(sectionId);
    setError("");
    setSuccess("");
    try {
      await generateSectionVideosAI(sectionId);
      setSuccess("تم توليد عناوين الفيديوهات بنجاح");
      await loadSections();
    } catch (e) {
      console.error(e);
      setError("فشل توليد عناوين الفيديوهات");
    } finally {
      setAiVideoLoading(null);
    }
  };

  const handleGenerateVideoDescription = async (videoId, videoTitle) => {
    setAiDescriptionLoading(videoId);
    setError("");
    try {
      const description = await generateVideoDescriptionAI(videoId);
      setSections((prev) =>
        prev.map((section) => ({
          ...section,
          videos: section.videos.map((v) =>
            v.id === videoId ? { ...v, description } : v
          ),
        }))
      );
      setSuccess(`تم توليد وصف للفيديو "${videoTitle}"`);
    } catch (e) {
      console.error(e);
      setError("فشل توليد الوصف");
    } finally {
      setAiDescriptionLoading(null);
    }
  };

  const handleGenerateThumbnail = async (videoId, videoTitle) => {
    setAiThumbnailLoading(videoId);
    setError("");
    try {
      const thumbnail_url = await generateVideoThumbnailAI(videoId);
      setSections((prev) =>
        prev.map((section) => ({
          ...section,
          videos: section.videos.map((v) =>
            v.id === videoId ? { ...v, thumbnail_url } : v
          ),
        }))
      );
      setSuccess(`تم توليد صورة مصغرة للفيديو "${videoTitle}"`);
    } catch (e) {
      console.error(e);
      setError("فشل توليد الصورة المصغرة");
    } finally {
      setAiThumbnailLoading(null);
    }
  };

  const handleGetThumbnail = async (videoId) => {
    try {
      const thumbnail_url = await getVideoThumbnail(videoId);
      setSections((prev) =>
        prev.map((section) => ({
          ...section,
          videos: section.videos.map((v) =>
            v.id === videoId ? { ...v, thumbnail_url } : v
          ),
        }))
      );
    } catch (e) {
      console.error(e);
      setError("فشل تحميل الصورة المصغرة");
    }
  };

  const handleUploadThumbnail = async (videoId, file) => {
    try {
      const thumbnail_url = await uploadVideoThumbnail(videoId, file);
      setSections((prev) =>
        prev.map((section) => ({
          ...section,
          videos: section.videos.map((v) =>
            v.id === videoId ? { ...v, thumbnail_url } : v
          ),
        }))
      );
      setSuccess("تم رفع الصورة المصغرة بنجاح");
    } catch (e) {
      console.error(e);
      setError("فشل رفع الصورة المصغرة");
    }
  };

  const handleDeleteThumbnail = async (videoId) => {
    if (!window.confirm("هل أنت متأكد من حذف الصورة المصغرة؟")) return;
    
    try {
      await deleteVideoThumbnail(videoId);
      setSections((prev) =>
        prev.map((section) => ({
          ...section,
          videos: section.videos.map((v) =>
            v.id === videoId ? { ...v, thumbnail_url: null } : v
          ),
        }))
      );
      setSuccess("تم حذف الصورة المصغرة بنجاح");
    } catch (e) {
      console.error(e);
      setError("فشل حذف الصورة المصغرة");
    }
  };

  const moveVideo = async (sectionIndex, videoIndex, direction) => {
    const updated = [...sections];
    const videos = [...updated[sectionIndex].videos];
    const target = videoIndex + direction;
    if (target < 0 || target >= videos.length) return;

    [videos[videoIndex], videos[target]] = [videos[target], videos[videoIndex]];
    updated[sectionIndex].videos = videos;
    setSections(updated);

    try {
      await reorderCourseVideos(
        updated[sectionIndex].id,
        videos.map((v, i) => ({ id: v.id, order: i + 1 }))
      );
      setSuccess("تم إعادة ترتيب الفيديوهات بنجاح");
    } catch (e) {
      console.error(e);
      setError("فشل إعادة الترتيب");
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleVideoClick = (video) => {
    setActiveVideo(video);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>جاري تحميل محتوى الكورس...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.pageTitle}>إدارة محتوى الكورس</h1>
            <p style={styles.pageSubtitle}>
              {courseTitle || `الكورس ID: ${courseId}`}
            </p>
          </div>
          <button 
            style={styles.backButton}
            onClick={() => navigate("/trainer/courses")}
          >
            ↩ العودة للقائمة
          </button>
        </div>
      </div>

      <div style={styles.content}>
        {error && (
          <div style={styles.errorMessage}>
            <span style={styles.errorIcon}>⚠️</span>
            {error}
          </div>
        )}
        
        {success && (
          <div style={styles.successMessage}>
            <span style={styles.successIcon}>✅</span>
            {success}
          </div>
        )}

        {/* إضافة سكشن جديد */}
        <div style={styles.addSectionCard}>
          <h2 style={styles.sectionTitle}>➕ إضافة سكشن جديد</h2>
          <div style={styles.addSectionForm}>
            <input
              placeholder="أدخل عنوان السكشن الجديد (مثال: مقدمة في البرمجة)"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              style={styles.sectionInput}
            />
            <button 
              onClick={handleAddSection}
              style={styles.addSectionButton}
            >
              + إضافة سكشن
            </button>
            <button 
              onClick={handleGenerateSectionsAI}
              disabled={aiLoading}
              style={aiLoading ? {...styles.aiButton, ...styles.aiButtonLoading} : styles.aiButton}
            >
              {aiLoading ? (
                <>
                  <span style={styles.smallSpinner}></span>
                  جاري التوليد...
                </>
              ) : (
                "⚡ توليد سكشنات بالذكاء الاصطناعي"
              )}
            </button>
          </div>
          <p style={styles.helpText}>
            أو استخدم الذكاء الاصطناعي لتوليد سكشنات منظمة تلقائيًا
          </p>
        </div>

        {/* قائمة السكشنات */}
        <div style={styles.sectionsContainer}>
          <h2 style={styles.sectionsTitle}>📁 السكشنات ({sections.length})</h2>
          
          {sections.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📚</div>
              <h3 style={styles.emptyTitle}>لا يوجد سكشنات بعد</h3>
              <p style={styles.emptyText}>ابدأ بإضافة أول سكشن لك</p>
            </div>
          ) : (
            <div style={styles.sectionsList}>
              {sections.map((section, sIndex) => (
                <div key={section.id} style={styles.sectionCard}>
                  <div 
                    style={styles.sectionHeader}
                    onClick={() => toggleSection(section.id)}
                  >
                    <div style={styles.sectionHeaderLeft}>
                      <span style={styles.sectionIcon}>
                        {expandedSections[section.id] ? "📂" : "📁"}
                      </span>
                      <div>
                        <h3 style={styles.sectionTitleText}>
                          {section.title}
                          <span style={styles.videoCount}>
                            ({section.videos?.length || 0} فيديو)
                          </span>
                        </h3>
                        <p style={styles.sectionDesc}>
                          السكشن {sIndex + 1} من {sections.length}
                        </p>
                      </div>
                    </div>
                    <div style={styles.sectionActions}>
                      <button
                        style={styles.sectionActionButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSection(sIndex, -1);
                        }}
                        disabled={sIndex === 0}
                      >
                        ⬆
                      </button>
                      <button
                        style={styles.sectionActionButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSection(sIndex, 1);
                        }}
                        disabled={sIndex === sections.length - 1}
                      >
                        ⬇
                      </button>
                      <button
                        style={styles.aiGenerateButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateVideosAI(section.id, section.title);
                        }}
                        disabled={aiVideoLoading === section.id}
                      >
                        {aiVideoLoading === section.id ? (
                          <span style={styles.tinySpinner}></span>
                        ) : (
                          "🎬 توليد فيديوهات"
                        )}
                      </button>
                      <button
                        style={styles.deleteSectionButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSection(section.id, section.title);
                        }}
                      >
                        🗑️
                      </button>
                      <span style={styles.expandIcon}>
                        {expandedSections[section.id] ? "▼" : "▶"}
                      </span>
                    </div>
                  </div>

                  {expandedSections[section.id] && (
                    <div style={styles.sectionContent}>
                      {/* قائمة الفيديوهات */}
                      {section.videos?.length > 0 ? (
                        <div style={styles.videosList}>
                          {section.videos.map((video, vIndex) => (
                            <div key={video.id} style={styles.videoCard}>
                              <div style={styles.videoHeader}>
                                <div style={styles.videoInfo}>
                                  <h4 style={styles.videoTitle}>
                                    <span style={styles.videoNumber}>{vIndex + 1}.</span>
                                    {video.title}
                                  </h4>
                                  <p style={styles.videoDesc}>
                                    {video.description || (
                                      <span style={styles.noDescription}>لا يوجد وصف</span>
                                    )}
                                  </p>
                                </div>
                                <div style={styles.videoActions}>
                                  <button
                                    style={styles.moveButton}
                                    onClick={() => moveVideo(sIndex, vIndex, -1)}
                                    disabled={vIndex === 0}
                                  >
                                    ⬆
                                  </button>
                                  <button
                                    style={styles.moveButton}
                                    onClick={() => moveVideo(sIndex, vIndex, 1)}
                                    disabled={vIndex === section.videos.length - 1}
                                  >
                                    ⬇
                                  </button>
                                  <button
                                    style={styles.playButton}
                                    onClick={() => handleVideoClick(video)}
                                  >
                                    ▶ تشغيل
                                  </button>
                                </div>
                              </div>

                              {/* الصورة المصغرة */}
                              <div style={styles.thumbnailSection}>
                                {video.thumbnail_url ? (
                                  <div style={styles.thumbnailWithControls}>
                                    <img
                                      src={video.thumbnail_url}
                                      alt="thumbnail"
                                      style={styles.thumbnailImage}
                                      onClick={() => window.open(video.thumbnail_url, '_blank')}
                                    />
                                    <div style={styles.thumbnailButtons}>
                                      <button
                                        style={styles.thumbnailButton}
                                        onClick={() => handleDeleteThumbnail(video.id)}
                                      >
                                        ❌ حذف
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={styles.thumbnailPlaceholder}>
                                    <span style={styles.placeholderIcon}>🖼️</span>
                                    <span style={styles.placeholderText}>لا توجد صورة مصغرة</span>
                                  </div>
                                )}
                                
                                <div style={styles.thumbnailControls}>
                                  <button
                                    style={styles.thumbnailControlButton}
                                    onClick={() => handleGetThumbnail(video.id)}
                                  >
                                    👁️ إظهار الصورة
                                  </button>
                                  <button
                                    style={styles.thumbnailControlButton}
                                    onClick={() => handleGenerateThumbnail(video.id, video.title)}
                                    disabled={aiThumbnailLoading === video.id}
                                  >
                                    {aiThumbnailLoading === video.id ? (
                                      <span style={styles.tinySpinner}></span>
                                    ) : (
                                      "🤖 توليد صورة AI"
                                    )}
                                  </button>
                                  <label style={styles.uploadThumbnailButton}>
                                    📁 رفع صورة
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) =>
                                        e.target.files &&
                                        handleUploadThumbnail(video.id, e.target.files[0])
                                      }
                                      style={styles.hiddenFileInput}
                                    />
                                  </label>
                                </div>
                              </div>

                              {/* وصف الفيديو */}
                              <div style={styles.descriptionSection}>
                                <p style={styles.descriptionLabel}>وصف الفيديو:</p>
                                <p style={styles.descriptionText}>
                                  {video.description || "لا يوجد وصف للفيديو بعد."}
                                </p>
                                <button
                                  style={styles.generateDescriptionButton}
                                  onClick={() => handleGenerateVideoDescription(video.id, video.title)}
                                  disabled={aiDescriptionLoading === video.id}
                                >
                                  {aiDescriptionLoading === video.id ? (
                                    <span style={styles.tinySpinner}></span>
                                  ) : (
                                    "🤖 توليد وصف بالذكاء الاصطناعي"
                                  )}
                                </button>
                              </div>

                              {/* زر حذف الفيديو */}
                              <button
                                style={styles.deleteVideoButton}
                                onClick={() => handleDeleteVideo(video.id, video.title)}
                              >
                                🗑️ حذف الفيديو
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={styles.emptyVideos}>
                          <p style={styles.emptyVideosText}>لا يوجد فيديوهات في هذا السكشن</p>
                        </div>
                      )}

                      {/* نموذج إضافة فيديو جديد */}
                      <AddVideoForm 
                        onSubmit={(v) => handleAddVideo(section.id, v)}
                        sectionTitle={section.title}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* مشغل الفيديو */}
        {activeVideo && (
          <div style={styles.videoPlayerModal}>
            <div style={styles.videoPlayerContent}>
              <div style={styles.videoPlayerHeader}>
                <h3>{activeVideo.title}</h3>
                <button 
                  style={styles.closePlayerButton}
                  onClick={() => setActiveVideo(null)}
                >
                  ✕
                </button>
              </div>
              {activeVideo.video_url ? (
                <video 
                  src={activeVideo.video_url} 
                  controls 
                  style={styles.videoPlayer}
                  autoPlay
                />
              ) : (
                <div style={styles.noVideo}>
                  <span style={styles.noVideoIcon}>🎬</span>
                  <p>لا يوجد فيديو مرفوع بعد</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= Add Video Form ================= */
function AddVideoForm({ onSubmit, sectionTitle }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const handleUpload = async () => {
    if (!file || !title) {
      alert("يرجى إدخال عنوان الفيديو ورفع ملف");
      return;
    }
    
    setLoading(true);
    try {
      const videoUrl = await uploadTrainerVideo(file);
      onSubmit({ title, description, video_url: videoUrl });
      setTitle("");
      setDescription("");
      setFile(null);
      setPreview(null);
      setExpanded(false);
    } catch (e) {
      console.error(e);
      alert("فشل رفع الفيديو");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.addVideoForm}>
      <div 
        style={styles.addVideoHeader}
        onClick={() => setExpanded(!expanded)}
      >
        <span style={styles.addVideoIcon}>➕</span>
        <span style={styles.addVideoTitle}>إضافة فيديو جديد للسكشن "{sectionTitle}"</span>
        <span style={styles.expandIcon}>{expanded ? "▼" : "▶"}</span>
      </div>
      
      {expanded && (
        <div style={styles.addVideoContent}>
          <div style={styles.formRow}>
            <input
              placeholder="عنوان الفيديو *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={styles.videoInput}
            />
            <textarea
              placeholder="وصف الفيديو (اختياري)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={styles.videoTextarea}
              rows="3"
            />
          </div>
          
          <div style={styles.fileUploadSection}>
            <label style={styles.fileUploadButton}>
              📁 اختر ملف فيديو
              <input
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                style={styles.hiddenFileInput}
              />
            </label>
            {file && (
              <span style={styles.fileName}>{file.name}</span>
            )}
          </div>
          
          {preview && (
            <div style={styles.previewSection}>
              <video 
                src={preview} 
                controls 
                style={styles.previewVideo}
              />
            </div>
          )}
          
          <button 
            onClick={handleUpload} 
            disabled={loading || !file || !title}
            style={loading ? {...styles.uploadButton, ...styles.uploadButtonLoading} : styles.uploadButton}
          >
            {loading ? (
              <>
                <span style={styles.smallSpinner}></span>
                جاري الرفع...
              </>
            ) : (
              "🚀 رفع الفيديو"
            )}
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    direction: 'rtl',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: '#f5f7fa',
    minHeight: '100vh',
  },
  header: {
    background: 'linear-gradient(135deg, #2c3e50 0%, #1a2530 100%)',
    color: 'white',
    padding: '25px 30px',
    borderRadius: '0 0 15px 15px',
    marginBottom: '30px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: '1.8rem',
    margin: '0 0 8px 0',
    fontWeight: '700',
  },
  pageSubtitle: {
    fontSize: '1rem',
    opacity: '0.9',
    margin: 0,
  },
  backButton: {
    padding: '10px 20px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 20px 40px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    color: '#666',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  smallSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
    marginRight: '8px',
  },
  tinySpinner: {
    width: '12px',
    height: '12px',
    border: '2px solid rgba(0, 0, 0, 0.1)',
    borderTop: '2px solid #333',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
  },
  errorMessage: {
    background: '#ffebee',
    color: '#c62828',
    padding: '15px 20px',
    borderRadius: '8px',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #f44336',
  },
  errorIcon: {
    fontSize: '1.5rem',
  },
  successMessage: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '15px 20px',
    borderRadius: '8px',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #4CAF50',
  },
  successIcon: {
    fontSize: '1.5rem',
  },
  addSectionCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '25px',
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '1.4rem',
    margin: '0 0 15px 0',
    color: '#333',
    fontWeight: '600',
  },
  addSectionForm: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    marginBottom: '10px',
  },
  sectionInput: {
    flex: 1,
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  },
  addSectionButton: {
    padding: '12px 25px',
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
  },
  aiButton: {
    padding: '12px 25px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
  },
  aiButtonLoading: {
    opacity: '0.7',
    cursor: 'not-allowed',
  },
  helpText: {
    fontSize: '0.9rem',
    color: '#666',
    margin: '5px 0 0 0',
  },
  sectionsContainer: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '25px',
  },
  sectionsTitle: {
    fontSize: '1.4rem',
    margin: '0 0 20px 0',
    color: '#333',
    fontWeight: '600',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#777',
  },
  emptyIcon: {
    fontSize: '3rem',
    opacity: '0.5',
    marginBottom: '15px',
  },
  emptyTitle: {
    fontSize: '1.3rem',
    margin: '0 0 10px 0',
    color: '#555',
  },
  emptyText: {
    fontSize: '1rem',
    opacity: '0.8',
  },
  sectionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  sectionCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  },
  sectionHeader: {
    padding: '20px',
    background: '#f8f9fa',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    borderBottom: '1px solid transparent',
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  sectionIcon: {
    fontSize: '1.8rem',
  },
  sectionTitleText: {
    margin: '0 0 5px 0',
    fontSize: '1.1rem',
    color: '#333',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  videoCount: {
    fontSize: '0.9rem',
    color: '#666',
    fontWeight: 'normal',
  },
  sectionDesc: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#666',
  },
  sectionActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionActionButton: {
    width: '35px',
    height: '35px',
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  aiGenerateButton: {
    padding: '8px 12px',
    background: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  deleteSectionButton: {
    width: '35px',
    height: '35px',
    background: '#ffebee',
    color: '#f44336',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  expandIcon: {
    fontSize: '1rem',
    color: '#666',
    marginRight: '10px',
  },
  sectionContent: {
    padding: '20px',
  },
  videosList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '20px',
  },
  videoCard: {
    border: '1px solid #f0f0f0',
    borderRadius: '8px',
    padding: '15px',
    background: '#f8f9ff',
  },
  videoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
  },
  videoInfo: {
    flex: 1,
  },
  videoTitle: {
    margin: '0 0 8px 0',
    fontSize: '1rem',
    color: '#333',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  videoNumber: {
    color: '#667eea',
    fontWeight: 'bold',
  },
  videoDesc: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#666',
    lineHeight: '1.5',
  },
  noDescription: {
    color: '#999',
    fontStyle: 'italic',
  },
  videoActions: {
    display: 'flex',
    gap: '8px',
  },
  moveButton: {
    width: '30px',
    height: '30px',
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playButton: {
    padding: '6px 12px',
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  thumbnailSection: {
    marginBottom: '15px',
  },
  thumbnailWithControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '10px',
  },
  thumbnailImage: {
    width: '150px',
    height: '90px',
    borderRadius: '8px',
    objectFit: 'cover',
    cursor: 'pointer',
    border: '2px solid #e0e0e0',
  },
  thumbnailButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  thumbnailButton: {
    padding: '6px 10px',
    background: '#ffebee',
    color: '#f44336',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  thumbnailPlaceholder: {
    width: '150px',
    height: '90px',
    borderRadius: '8px',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    marginBottom: '10px',
  },
  placeholderIcon: {
    fontSize: '1.5rem',
    marginBottom: '5px',
  },
  placeholderText: {
    fontSize: '0.8rem',
  },
  thumbnailControls: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  thumbnailControlButton: {
    padding: '6px 12px',
    background: '#f5f5f5',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  uploadThumbnailButton: {
    padding: '6px 12px',
    background: '#e3f2fd',
    color: '#1976d2',
    border: '1px solid #bbdefb',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  },
  hiddenFileInput: {
    display: 'none',
  },
  descriptionSection: {
    marginBottom: '15px',
  },
  descriptionLabel: {
    fontWeight: '600',
    color: '#333',
    margin: '0 0 5px 0',
    fontSize: '0.9rem',
  },
  descriptionText: {
    margin: '0 0 10px 0',
    fontSize: '0.9rem',
    color: '#666',
    lineHeight: '1.5',
  },
  generateDescriptionButton: {
    padding: '6px 12px',
    background: '#fff3e0',
    color: '#ef6c00',
    border: '1px solid #ffe0b2',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  deleteVideoButton: {
    padding: '8px 15px',
    background: '#ffebee',
    color: '#f44336',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    width: '100%',
  },
  emptyVideos: {
    textAlign: 'center',
    padding: '20px',
    color: '#999',
  },
  emptyVideosText: {
    margin: 0,
  },
  videoPlayerModal: {
    position: 'fixed',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  videoPlayerContent: {
    background: 'white',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '800px',
    overflow: 'hidden',
  },
  videoPlayerHeader: {
    padding: '15px 20px',
    background: '#333',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  closePlayerButton: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '1.5rem',
    cursor: 'pointer',
  },
  videoPlayer: {
    width: '100%',
    height: '450px',
  },
  noVideo: {
    padding: '40px',
    textAlign: 'center',
    color: '#666',
  },
  noVideoIcon: {
    fontSize: '3rem',
    marginBottom: '15px',
  },
  addVideoForm: {
    border: '2px dashed #e0e0e0',
    borderRadius: '10px',
    marginTop: '20px',
  },
  addVideoHeader: {
    padding: '15px',
    background: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
  },
  addVideoIcon: {
    fontSize: '1.2rem',
  },
  addVideoTitle: {
    flex: 1,
    fontSize: '1rem',
    fontWeight: '600',
    color: '#333',
  },
  addVideoContent: {
    padding: '20px',
  },
  formRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '15px',
  },
  videoInput: {
    padding: '10px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
  },
  videoTextarea: {
    padding: '10px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  fileUploadSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '15px',
  },
  fileUploadButton: {
    padding: '10px 20px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'inline-block',
  },
  fileName: {
    fontSize: '0.9rem',
    color: '#666',
  },
  previewSection: {
    marginBottom: '15px',
  },
  previewVideo: {
    width: '100%',
    maxHeight: '300px',
    borderRadius: '8px',
  },
  uploadButton: {
    padding: '12px 25px',
    background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  uploadButtonLoading: {
    opacity: '0.7',
    cursor: 'not-allowed',
  },
};

// إضافة تأثيرات Hover
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      .input-focus:focus, .textarea-focus:focus {
        outline: none;
        border-color: #667eea !important;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
      }
      
      .back-btn-hover:hover {
        background: rgba(255, 255, 255, 0.2) !important;
        transform: translateX(5px);
      }
      
      .add-section-btn-hover:hover:not(:disabled) {
        background: #2E7D32 !important;
        transform: translateY(-2px);
      }
      
      .ai-btn-hover:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3) !important;
      }
      
      .section-card-hover:hover {
        border-color: #667eea !important;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1) !important;
      }
      
      .section-header-hover:hover {
        background: #e8eaff !important;
      }
      
      .action-btn-hover:hover:not(:disabled) {
        transform: scale(1.1);
      }
      
      .delete-btn-hover:hover {
        background: #f44336 !important;
        color: white !important;
      }
      
      .video-card-hover:hover {
        border-color: #667eea !important;
        background: #f0f3ff !important;
      }
      
      .thumbnail-btn-hover:hover {
        background: #ffcdd2 !important;
      }
      
      .thumbnail-control-btn-hover:hover {
        background: #e0e0e0 !important;
      }
      
      .upload-thumbnail-btn-hover:hover {
        background: #bbdefb !important;
      }
      
      .generate-desc-btn-hover:hover:not(:disabled) {
        background: #ffcc80 !important;
      }
      
      .play-btn-hover:hover {
        background: #2E7D32 !important;
        transform: scale(1.05);
      }
      
      .file-upload-btn-hover:hover {
        background: #764ba2 !important;
        transform: translateY(-2px);
      }
      
      .upload-video-btn-hover:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(76, 175, 80, 0.3) !important;
      }
      
      .ai-generate-btn-hover:hover:not(:disabled) {
        background: #1976d2 !important;
      }
      
      @media (max-width: 768px) {
        .section-header-responsive {
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 15px !important;
        }
        
        .section-actions-responsive {
          width: 100%;
          justify-content: space-between !important;
        }
        
        .video-header-responsive {
          flex-direction: column !important;
          gap: 15px !important;
        }
        
        .video-actions-responsive {
          width: 100%;
          justify-content: center !important;
        }
        
        .thumbnail-controls-responsive {
          flex-direction: column !important;
          align-items: flex-start !important;
        }
      }
      
      @media (max-width: 480px) {
        .add-section-form-responsive {
          flex-direction: column !important;
        }
        
        .video-player-responsive {
          height: 300px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // إضافة classes للعناصر
    setTimeout(() => {
      document.querySelectorAll('input').forEach(el => {
        el.className = (el.className || '') + ' input-focus';
      });
      document.querySelectorAll('textarea').forEach(el => {
        el.className = (el.className || '') + ' textarea-focus';
      });
      
      // إضافة classes للعناصر المختلفة
      const elements = [
        { selector: '[style*="backButton"]', className: 'back-btn-hover' },
        { selector: '[style*="addSectionButton"]', className: 'add-section-btn-hover' },
        { selector: '[style*="aiButton"]', className: 'ai-btn-hover' },
        { selector: '[style*="sectionCard"]', className: 'section-card-hover' },
        { selector: '[style*="sectionHeader"]', className: 'section-header-hover' },
        { selector: '[style*="sectionActionButton"]', className: 'action-btn-hover' },
        { selector: '[style*="moveButton"]', className: 'action-btn-hover' },
        { selector: '[style*="deleteSectionButton"]', className: 'delete-btn-hover' },
        { selector: '[style*="videoCard"]', className: 'video-card-hover' },
        { selector: '[style*="thumbnailButton"]', className: 'thumbnail-btn-hover' },
        { selector: '[style*="thumbnailControlButton"]', className: 'thumbnail-control-btn-hover' },
        { selector: '[style*="uploadThumbnailButton"]', className: 'upload-thumbnail-btn-hover' },
        { selector: '[style*="generateDescriptionButton"]', className: 'generate-desc-btn-hover' },
        { selector: '[style*="playButton"]', className: 'play-btn-hover' },
        { selector: '[style*="fileUploadButton"]', className: 'file-upload-btn-hover' },
        { selector: '[style*="uploadButton"]', className: 'upload-video-btn-hover' },
        { selector: '[style*="aiGenerateButton"]', className: 'ai-generate-btn-hover' },
        { selector: '[style*="deleteVideoButton"]', className: 'delete-btn-hover' },
      ];
      
      elements.forEach(({ selector, className }) => {
        document.querySelectorAll(selector).forEach(el => {
          el.className = (el.className || '') + ' ' + className;
        });
      });
      
      // Responsive classes
      if (window.innerWidth <= 768) {
        const sectionHeaders = document.querySelectorAll('[style*="sectionHeader"]');
        const sectionActions = document.querySelectorAll('[style*="sectionActions"]');
        const videoHeaders = document.querySelectorAll('[style*="videoHeader"]');
        const videoActions = document.querySelectorAll('[style*="videoActions"]');
        const thumbnailControls = document.querySelectorAll('[style*="thumbnailControls"]');
        
        sectionHeaders.forEach(el => {
          el.className = (el.className || '') + ' section-header-responsive';
        });
        sectionActions.forEach(el => {
          el.className = (el.className || '') + ' section-actions-responsive';
        });
        videoHeaders.forEach(el => {
          el.className = (el.className || '') + ' video-header-responsive';
        });
        videoActions.forEach(el => {
          el.className = (el.className || '') + ' video-actions-responsive';
        });
        thumbnailControls.forEach(el => {
          el.className = (el.className || '') + ' thumbnail-controls-responsive';
        });
      }
      
      if (window.innerWidth <= 480) {
        const addSectionForm = document.querySelector('[style*="addSectionForm"]');
        const videoPlayers = document.querySelectorAll('[style*="videoPlayer"]');
        
        if (addSectionForm) {
          addSectionForm.className = (addSectionForm.className || '') + ' add-section-form-responsive';
        }
        videoPlayers.forEach(el => {
          el.className = (el.className || '') + ' video-player-responsive';
        });
      }
    }, 200);
  }, 100);
}